import { Lock } from "@tamagui/lucide-icons";
import { FormikProvider, useFormik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Heading, Image, Text, View, XStack, YStack } from "tamagui";
import { GradientButton, OutlinedButton } from "tamagui-config";

import qrCodeExampleImage from "../../assets/images/qr-code-example.png";
import ConfirmDialog from "../../components/ConfirmDialog";
import { FormikInput } from "../../components/FormikInput";
import { StatusPill } from "../../components/StatusPill";
import useFieldValidationSchema from "../../hooks/useFieldValidationSchema";
import { useShowToast } from "../../hooks/useShowToast";

export const TwoFactorAuthentication = () => {
  const [isTotpEnabled, setIsTotpEnabled] = useState(false); // TODO: connect to backend
  const { t } = useTranslation();

  return (
    <YStack gap="$4">
      <YStack gap="$2">
        <Text fontSize="$5.5">{t("sidebar.settings.settings")}</Text>
        <Heading fontSize="$8">
          {t("sidebar.settings.twoFactorAuthentication")}
        </Heading>
      </YStack>
      {isTotpEnabled ? (
        <TotpManagement onDisable={() => setIsTotpEnabled(false)} />
      ) : (
        <TotpSetup onEnable={() => setIsTotpEnabled(true)} />
      )}
    </YStack>
  );
};

type TotpSetupFormValues = {
  code: string;
};

const TotpSetup = ({ onEnable }: { onEnable: (code: string) => void }) => {
  const { totpSetupValidationSchema } = useFieldValidationSchema();
  const { showErrorToast } = useShowToast();
  const { t } = useTranslation();

  const onSubmit = async ({ code }: TotpSetupFormValues) => {
    try {
      onEnable(code);
    } catch (error) {
      showErrorToast(
        typeof error === "object" && "message" in (error as any)
          ? (error as any).message
          : error,
      );
    }
  };

  const formik = useFormik<TotpSetupFormValues>({
    initialValues: {
      code: "",
    },
    enableReinitialize: true,
    validationSchema: totpSetupValidationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit,
  });

  return (
    <XStack
      gap="$8"
      alignItems="center"
      backgroundColor="$transparentBorder"
      padding="$3.5"
      borderRadius="$6"
    >
      <View
        width={240}
        height={240}
        alignItems="center"
        justifyContent="center"
        borderRadius="$5"
        backgroundColor="$white"
      >
        <Image
          source={{ uri: qrCodeExampleImage, width: 384, height: 384 }}
          width={192}
          height={192}
          resizeMode="stretch"
        />
      </View>
      <YStack gap="$6" flex={1}>
        <YStack gap="$3.5">
          <Text fontSize="$5.5">
            {t("twoFactorAuthentication.totpSetup.instructions.0")}
          </Text>
          <Text color="$white10">
            {t("twoFactorAuthentication.totpSetup.instructions.1")}
          </Text>
        </YStack>
        <YStack gap="$3.5">
          <Text>{t("twoFactorAuthentication.totpSetup.instructions.2")}</Text>
          <XStack gap="$3.5">
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit} style={{ flexGrow: 1 }}>
                <FormikInput
                  fieldName="code"
                  placeholder={t(
                    "twoFactorAuthentication.totpSetup.codePlaceholder",
                  )}
                />
              </form>
            </FormikProvider>
            <GradientButton
              elevated
              rounded
              width={180}
              onPress={(event) => {
                event.preventDefault();
                formik.handleSubmit();
              }}
            >
              {t("twoFactorAuthentication.totpSetup.verifyButton")}
            </GradientButton>
          </XStack>
        </YStack>
      </YStack>
    </XStack>
  );
};

const TotpManagement = ({ onDisable }: { onDisable: () => void }) => {
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);
  const { showErrorToast } = useShowToast();
  const { t } = useTranslation();

  const onDisablePress = () => {
    setIsDisableDialogOpen(true);
  };

  const onDisableConfirmPress = () => {
    try {
      onDisable();
    } catch (error) {
      showErrorToast(
        typeof error === "object" && "message" in (error as any)
          ? (error as any).message
          : error,
      );
    }
  };

  return (
    <>
      <YStack
        gap="$5"
        backgroundColor="$transparentBorder"
        padding="$3.5"
        borderRadius="$6"
      >
        <XStack alignItems="center" justifyContent="space-between">
          <Lock size={42} />
          <StatusPill success hideDot>
            {t("twoFactorAuthentication.totpManagement.confirmed")}
          </StatusPill>
        </XStack>
        <XStack alignItems="center" justifyContent="space-between">
          <YStack gap="$2">
            <Text fontSize="$5.5" fontWeight="bold">
              {t("twoFactorAuthentication.totpManagement.title")}
            </Text>
            <Text>
              {t("twoFactorAuthentication.totpManagement.description")}
            </Text>
          </YStack>
          <OutlinedButton rounded onPress={onDisablePress}>
            {t("twoFactorAuthentication.totpManagement.disableButton")}
          </OutlinedButton>
        </XStack>
      </YStack>

      <ConfirmDialog
        open={isDisableDialogOpen}
        title={t("twoFactorAuthentication.totpManagement.disableDialog.title")}
        titleProps={{
          whiteSpace: "pre-line",
        }}
        description={t(
          "twoFactorAuthentication.totpManagement.disableDialog.description",
        )}
        confirmButtonText={t(
          "twoFactorAuthentication.totpManagement.disableDialog.confirmButton",
        )}
        onOpenChange={setIsDisableDialogOpen}
        onConfirm={onDisableConfirmPress}
      />
    </>
  );
};

export default TwoFactorAuthentication;
